import React, { useContext, useEffect, useState } from 'react'

import { maintainAuthDomAttributes$ } from '../lib/auth/dom-attributes'
import { latestDispensary$, setDispensary } from '../lib/dispensaries/state'
import { maintainHeaderStyleCssVariables$ } from '../lib/header/css-variables'
import { utilityMenuState$ } from '../lib/header/utility-menu'
import { ketchExperienceState$ } from '../lib/ketch/state'
import { ketchScriptTag$ } from '../lib/ketch/tag'
import { onboardingCleanup$ } from '../lib/onboarding/cleanup'
import { onboardRequestState$ } from '../lib/onboarding/request'
import { LocationContext } from '../lib/pages/context'
import { publishProvisioningNeededEvent$ } from '../lib/provisioning/state'
import { dispensaryLogoCdn$, sanityDispensaryCdn$ } from '../lib/sanity-dispensaries/query'
import { latestDispensarySlug$ } from '../lib/sanity-dispensaries/state'
import { supportModalState$ } from '../lib/support/modal/state'

interface GlobalSubscriptionsState {
  dispensary: boolean
}

const initialState: GlobalSubscriptionsState = {
  dispensary: false,
}

/**
 * Component to hold global RxJS subscriptions. Intended for use in root element wrapper.
 */
export const GlobalSubscriptions: React.FC = () => {
  // use LocationContext directly - useLocation throws an error if context value is null
  const { location } = useContext(LocationContext)
  const [state, setState] = useState<GlobalSubscriptionsState>(initialState)

  useEffect(() => {
    setState((prev) => {
      const dispensary = location?.pathname.startsWith('/u/') ?? false
      return prev.dispensary === dispensary ? prev : { dispensary }
    })
  }, [location?.pathname])

  useEffect(() => {
    const subscriptions = [
      ketchScriptTag$.subscribe(),
      // avoid setting up event handlers repeatedly on page navigation
      ketchExperienceState$.subscribe(),
      maintainHeaderStyleCssVariables$.subscribe(),
      maintainAuthDomAttributes$.subscribe(),
      publishProvisioningNeededEvent$.subscribe(),
      supportModalState$.subscribe(),
      utilityMenuState$.subscribe(),
      onboardingCleanup$.subscribe(),
      onboardRequestState$.subscribe(),
    ]
    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe())
    }
  }, [])

  useEffect(() => {
    if (!state.dispensary) {
      return
    }
    const subscriptions = [
      // hold subscription to dispensary (mirrors context value)
      latestDispensary$.subscribe(),
      // hold subscription to slug for use in Sanity dispensary queries
      latestDispensarySlug$.subscribe(),
      dispensaryLogoCdn$.subscribe(),
      sanityDispensaryCdn$.subscribe(),
    ]
    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe())
      // clear dispensary context on unmount. This is important when using browser navigation to exit
      // a dispensary and return to the main site.
      setDispensary(undefined)
    }
  }, [state.dispensary])

  return null
}
